import { access } from 'node:fs/promises';
import { join } from 'node:path';
import { readFile } from 'node:fs/promises';
import type { ProjectInfo } from './context-types.js';

export type ProjectType = 'node' | 'rust' | 'python' | 'go' | 'unknown';

export async function detectProjectType(workspaceRoot: string): Promise<ProjectType> {
  const markers: Array<[string, ProjectType]> = [
    ['package.json', 'node'],
    ['Cargo.toml', 'rust'],
    ['pyproject.toml', 'python'],
    ['go.mod', 'go'],
  ];
  for (const [marker, type] of markers) {
    try {
      await access(join(workspaceRoot, marker));
      return type;
    } catch {
      // Continue checking the next marker.
    }
  }
  return 'unknown';
}

async function exists(path: string): Promise<boolean> {
  try {
    await access(path);
    return true;
  } catch {
    return false;
  }
}

async function readPackageJson(workspaceRoot: string): Promise<Record<string, unknown> | null> {
  try {
    return JSON.parse(await readFile(join(workspaceRoot, 'package.json'), 'utf8')) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function detectFrameworks(pkg: Record<string, unknown> | null): string[] {
  if (!pkg) return [];
  const deps = {
    ...(pkg.dependencies as Record<string, string> | undefined),
    ...(pkg.devDependencies as Record<string, string> | undefined),
  };
  const frameworks: string[] = [];
  if (deps.next) frameworks.push('Next.js');
  if (deps.react) frameworks.push('React');
  if (deps.vite) frameworks.push('Vite');
  if (deps.vue) frameworks.push('Vue');
  if (deps.svelte) frameworks.push('Svelte');
  if (deps.turbo) frameworks.push('Turborepo');
  if (deps.typescript) frameworks.push('TypeScript');
  return frameworks;
}

export async function detectProjectInfo(workspaceRoot: string): Promise<ProjectInfo> {
  const type = await detectProjectType(workspaceRoot);
  const pkg = await readPackageJson(workspaceRoot);
  const checks = await Promise.all([
    exists(join(workspaceRoot, 'pnpm-lock.yaml')),
    exists(join(workspaceRoot, 'package-lock.json')),
    exists(join(workspaceRoot, 'yarn.lock')),
    exists(join(workspaceRoot, 'bun.lockb')),
    exists(join(workspaceRoot, 'Cargo.lock')),
    exists(join(workspaceRoot, 'poetry.lock')),
    exists(join(workspaceRoot, 'go.mod')),
    exists(join(workspaceRoot, 'pnpm-workspace.yaml')),
    exists(join(workspaceRoot, 'turbo.json')),
  ]);
  const [pnpm, npm, yarn, bun, cargo, poetry, go, pnpmWorkspace, turbo] = checks;
  const packageManager = pnpm ? 'pnpm'
    : npm ? 'npm'
      : yarn ? 'yarn'
        : bun ? 'bun'
          : cargo ? 'cargo'
            : poetry ? 'poetry'
              : go ? 'go'
                : type === 'python' ? 'pip'
                  : 'unknown';
  const languages = new Set<string>();
  if (type === 'node') {
    languages.add('JavaScript');
    if (await exists(join(workspaceRoot, 'tsconfig.json'))) languages.add('TypeScript');
  }
  if (type === 'python') languages.add('Python');
  if (type === 'rust') languages.add('Rust');
  if (type === 'go') languages.add('Go');

  return {
    root: workspaceRoot,
    type,
    packageManager,
    frameworks: detectFrameworks(pkg),
    languages: [...languages],
    isMonorepo: Boolean(pnpmWorkspace || turbo || pkg?.workspaces),
    workspaceFiles: [
      pnpmWorkspace ? 'pnpm-workspace.yaml' : '',
      turbo ? 'turbo.json' : '',
    ].filter(Boolean),
  };
}
